import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  headingAnimation,
  subheadingParent,
  supportedText,
} from "@/animation/textAnimation";
import ContactForm from "@/shared/ContactForm/ContactForm";
import LucideIcon from "@/utils/LucideIcon";
import TestimonialCarousel from "@/shared/Carousel/TestimonialCarousel";
import CountUpNumber from "@/shared/CountUpData/CountUpData";
import { contactInfo, iconBgVariants, stats } from "@/data/contactUs";

const ContactUs = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const,
        staggerChildren: 0.15,
      },
    },
  };

  const statVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1,
        ease: "easeOut" as const,
      },
    },
  };

  return (
    <div className="relative text-gray-900 overflow-hidden">
      <div className="container">
        {/* Hero Section - keeping original spacing */}
        <section className="relative min-h-screen flex justify-center">
          <div className="overflow-hidden py-2 mt-8 md:mt-0 md:pt-12">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={isLoaded ? "visible" : "hidden"}
            >
              <div className="container mx-auto px-4 text-center">
                {/* Badge */}
                <motion.div
                  custom={0}
                  {...headingAnimation}
                  className="inline-flex items-center gap-2 bg-pink-200/10 hover:bg-pink-200/20 backdrop-blur-md rounded-full px-6 py-3 border border-blue-400/40 mb-5 transition-all duration-300"
                >
                  <LucideIcon
                    name="Phone"
                    className="w-5 h-5 text-purple-500/90 hover:text-purple-500/90 mr-3"
                  />
                  <span className="text-blue-600 font-medium font-inter">
                    Contact Us
                  </span>
                </motion.div>

                {/* Headline */}
                <motion.h1
                  custom={1}
                  {...headingAnimation}
                  className="text-4xl md:text-6xl lg:text-8xl font-bold mb-8 leading-normal"
                >
                  <span className="text-secondary-gradient bg-clip-text text-transparent font-inter block leading-normal">
                    Let's Create Something
                  </span>
                  <span className="text-2xl md:text-4xl xl:text-5xl text-secondary-gradient bg-clip-text text-transparent font-inter block leading-loose pb-1">
                    Amazing Together
                  </span>
                </motion.h1>

                {/* Subtext */}
                <motion.p
                  custom={2}
                  {...headingAnimation}
                  className="text-lg md:text-xl text-gray-600/80 mb-12 max-w-4xl mx-auto leading-relaxed font-inter"
                >
                  We're here to bring your vision to life with customized
                  merchandise and corporate gifts. Let's start your journey
                  today!
                </motion.p>
              </div>
            </motion.div>
            <ContactForm showField={true} />
          </div>
        </section>

        {/* Other Ways to Reach Us Section - keeping position but standardizing internal spacing */}
        <section className="relative py-16 lg:py-20 bg-wave-gradient">
          <div className="container mx-auto px-4">
            <motion.div className="text-center md:text-start mb-20">
              <motion.h2 {...subheadingParent}>
                <div className="flex items-center md:text-left justify-center md:justify-start mb-4">
                  <LucideIcon
                    name="Route"
                    className="w-10 h-10 text-blue-900 hover:text-blue-900 hidden md:inline-block mr-3"
                  />
                  <h2 className="text-4xl md:text-5xl font-bold font-inter text-secondary-gradient bg-clip-text text-transparent pb-1">
                    Other Ways to Reach Us
                  </h2>
                </div>
              </motion.h2>
              <motion.div
                className="w-24 h-1 bg-secondary-gradient mx-auto md:mx-0 md:ml-14 mb-8 rounded-xl"
                initial={{ width: 0 }}
                whileInView={{ width: 96 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                viewport={{ amount: 0.2 }}
              ></motion.div>
              <motion.p
                {...supportedText}
                className="text-xl text-gray-600 max-w-3xl leading-relaxed font-inter"
              >
                Reach out, we're just a step away.
              </motion.p>
            </motion.div>

            <div className="grid sm:grid-cols-1 lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  className="max-w-md mx-auto relative overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white rounded-xl"
                >
                  {/* Gradient Background */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${iconBgVariants[index % iconBgVariants.length]
                      } opacity-5`}
                  ></div>

                  <div className="p-8 relative">
                    <div className="flex items-center gap-4 mb-6">
                      <div
                        className={`w-14 h-14 bg-gradient-to-br ${iconBgVariants[index % iconBgVariants.length]
                          } rounded-2xl flex items-center justify-center`}
                      >
                        {<info.icon className="text-white" />}
                      </div>

                      <h3 className="text-2xl font-bold text-gray-800">
                        {info.title}
                      </h3>
                    </div>
                    <p className="text-gray-500 text-md mb-3">
                      {info.description}
                    </p>
                    <p className="text-gray-800 text-md font-medium">
                      {info.info.split("\n").map((line, idx) => (
                        <span key={idx} className="block">
                          {line}
                        </span>
                      ))}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section - standardized spacing */}
        <section className="relative py-16 lg:py-20">
          <div className="container mx-auto px-4 text-center">
            <motion.div className="text-center md:text-start mb-20">
              <motion.h2 {...subheadingParent}>
                <div className="flex items-center md:text-left justify-center md:justify-start mb-4">
                  <LucideIcon
                    name="Route"
                    className="w-10 h-10 text-blue-900 hover:text-blue-900 hidden md:inline-block mr-3"
                  />
                  <h2 className="text-4xl md:text-5xl font-bold font-inter text-secondary-gradient bg-clip-text text-transparent pb-1">
                    Why Choose Us
                  </h2>
                </div>
              </motion.h2>
              <motion.div
                className="w-24 h-1 bg-secondary-gradient mx-auto md:mx-0 md:ml-14 mb-8 rounded-xl"
                initial={{ width: 0 }}
                whileInView={{ width: 96 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                viewport={{ amount: 0.2 }}
              ></motion.div>
              <motion.p
                {...supportedText}
                className="text-xl text-gray-600 max-w-3xl leading-relaxed font-inter"
              >
                We don't just deliver services, we build long-lasting
                partnerships. Blending creativity with expertise, we craft
                solutions that truly stand out.
              </motion.p>
            </motion.div>

            <section className="py-4 relative">
              <div className="max-w-6xl mx-auto px-4">
                <motion.div
                  className="grid grid-cols-1 md:grid-cols-3 gap-10"
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.4 }}
                >
                  {stats.map((stat, index) => {
                    const IconComponent = stat.icon;

                    return (
                      <motion.div
                        key={index}
                        className="text-center group"
                        variants={statVariants}
                      >
                        <motion.div
                          className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 mx-auto mb-4 flex items-center justify-center overflow-hidden"
                          whileHover="hover"
                          initial="initial"
                          animate="initial"
                          variants={{
                            hover: { scale: 1.05 },
                            initial: { scale: 1 },
                          }}
                        >
                          <IconComponent className="h-8 w-8 text-white z-10 relative" />

                          <motion.div
                            className="absolute top-0 left-[-65%] w-[30%] h-full bg-white/60 z-20 pointer-events-none"
                            style={{ rotate: "0deg" }}
                            variants={{
                              hover: {
                                left: "100%",
                                transition: {
                                  duration: 0.4,
                                  ease: "easeInOut" as const,
                                },
                              },
                            }}
                          />
                        </motion.div>

                        <div className="text-3xl font-bold text-gray-900 mb-1">
                          {typeof stat.number === "number" ? (
                            <CountUpNumber
                              to={stat.number}
                              suffix={stat.suffix}
                            />
                          ) : (
                            <p className="text-2xl md:text-4xl font-bold">
                              {stat.number}
                            </p>
                          )}
                        </div>
                        <div className="text-gray-600 text-md font-medium">
                          {stat.label}
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              </div>
            </section>
          </div>
        </section>
      </div>
      <div className="py-16 lg:py-20">
        <TestimonialCarousel />
      </div>
    </div>
  );
};

export default ContactUs;
